// BEAD Performance Measures.
//
// Three views:
//   1. BSL Challenges — addresses where SecureNet has an ACTIVE customer at a
//                       BSL the FCC classifies as unserved/underserved.
//                       Each row is a candidate FCC fabric correction or
//                       BEAD pre-award challenge.
//   2. Compliance Samples — quarterly stratified-random samples from
//                       bead_location per NTIA Performance Measures Policy
//                       Notice (Sept 2025).
//   3. Algorithm — auditor-grade documentation of the random sampling
//                       methodology, seed-reproducibility, statutory
//                       citations, and verification protocol.

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

// ─── Types ────────────────────────────────────────────────────────────────

type Summary = {
  total: number;
  tested_count: number;
  by_status: Array<{ status: string; n: string }>;
  by_zip: Array<{ core_zip: string; n: string; unique_bsls: string }>;
  by_served_status: Array<{ fcc_served_status: string; n: string; unique_bsls: string }>;
  by_tauc_category: Array<{ tauc_device_category: string; n: string }>;
};

type FilingBatch = {
  id: string;
  name: string;
  filing_type: 'crowdsource' | 'challenge';
  methodology: string;
  data_vintage: string;
  status: 'draft' | 'built' | 'submitted' | 'accepted' | 'rejected' | 'withdrawn';
  member_count: number | string;
  built_at: string | null;
  built_member_count: number | null;
  built_sha256: string | null;
  submitted_at: string | null;
  submitted_confirmation: string | null;
  created_at: string;
};

type ProviderSettings = {
  bdc_provider_id: string;
  brand_name: string;
  default_technology: number;
  isp_legal_name: string;
  fcc_frn: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  filer_kind: string;
  updated_at?: string;
};

type Challenge = {
  bizrescode: 'R' | 'B' | 'X' | null;
  technology: number | null;
  evidence_description: string | null;
  id: string;
  core_customer_id: string | null;
  core_customer_name: string | null;
  core_account_status: string | null;
  core_monthly_total: string | null;
  core_activated_at: string | null;
  core_address: string;
  core_unit: string | null;
  core_city: string | null;
  core_zip: string | null;
  core_lat: number | null;
  core_lng: number | null;
  core_on_net: boolean;
  fcc_location_id: string;
  fcc_address: string;
  fcc_dl_mbps: number;
  fcc_ul_mbps: number;
  fcc_served_status: string;
  match_method: string;
  tauc_device_id: string | null;
  tauc_device_model: string | null;
  tauc_device_category: 'AGINET' | 'DECO' | 'UNKNOWN' | null;
  cpe_status: 'unresolved' | 'tauc_managed' | 'byod' | 'error';
  status: string;
  test_count: string;
  latest_test_at: string | null;
};

type Project = {
  id: string;
  project_code: string;
  project_name: string;
  county: string;
  tech_type: string;
  location_count: string;
  awarded_count: string;
  unserved_count: string;
  underserved_count: string;
};

type Sample = {
  id: string;
  project_id: string;
  project_code: string;
  project_name: string;
  quarter: string;
  population_size: number;
  sample_size: number;
  member_count: string;
  tested_member_count: string;
  status: string;
  drawn_at: string;
  seed: string;
};

// ─── Design tokens ────────────────────────────────────────────────────────

const C = {
  bg: '#f8fafc',
  card: '#ffffff',
  border: '#e2e8f0',
  borderHover: '#cbd5e1',
  text: '#0f172a',
  textMuted: '#64748b',
  textFaint: '#94a3b8',
  accent: '#1e3a8a',
  accentHover: '#1e40af',
  green: '#059669',
  amber: '#d97706',
  red: '#dc2626',
  shadow: '0 1px 3px rgba(15,23,42,0.04), 0 1px 2px rgba(15,23,42,0.06)',
  shadowHover: '0 4px 12px rgba(15,23,42,0.08)',
};

const SERVED_COLORS: Record<string, { bg: string; tx: string }> = {
  unserved:    { bg: '#fee2e2', tx: '#991b1b' },
  underserved: { bg: '#fef3c7', tx: '#92400e' },
  served:      { bg: '#d1fae5', tx: '#065f46' },
};

const STATUS_COLORS: Record<string, { bg: string; tx: string; label: string }> = {
  new:              { bg: '#dbeafe', tx: '#1e3a8a', label: 'New' },
  reviewed:         { bg: '#e0e7ff', tx: '#4338ca', label: 'Reviewed' },
  ready_to_submit:  { bg: '#fef3c7', tx: '#92400e', label: 'Ready' },
  submitted:        { bg: '#cffafe', tx: '#155e75', label: 'Submitted' },
  accepted:         { bg: '#d1fae5', tx: '#065f46', label: 'Accepted' },
  rejected:         { bg: '#fee2e2', tx: '#991b1b', label: 'Rejected' },
  duplicate:        { bg: '#f3f4f6', tx: '#6b7280', label: 'Duplicate' },
};

// ─── Top-level page ────────────────────────────────────────────────────────

type TabKey = 'challenges' | 'samples' | 'batches' | 'algorithm' | 'settings';

export default function PerformanceMeasuresPage() {
  const [tab, setTab] = useState<TabKey>('challenges');

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1600, margin: '0 auto', minHeight: '100vh' }}>
      <header style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: C.text, letterSpacing: '-0.01em' }}>
            BEAD Performance Measures
          </h1>
          <span style={{ fontSize: 11, color: C.textMuted, padding: '2px 8px',
                         background: '#e0e7ff', borderRadius: 3, fontWeight: 600,
                         textTransform: 'uppercase', letterSpacing: '.04em' }}>
            NTIA Sept 2025
          </span>
        </div>
        <p style={{ fontSize: 13, color: C.textMuted, marginTop: 6, maxWidth: 820, lineHeight: 1.5 }}>
          Quarterly stratified-random BSL sampling for federal Performance Measures compliance,
          plus an FCC fabric-correction workflow surfacing locations the BDC marks unserved that
          we actively serve. All speed-test evidence is captured through TAUC and tied back to
          the FCC <code style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 11 }}>location_id</code> for audit.
        </p>
      </header>

      <TabStrip current={tab} onChange={setTab} />

      <div style={{ marginTop: 4 }}>
        {tab === 'challenges' && <ChallengesView />}
        {tab === 'batches'    && <FilingBatchesView />}
        {tab === 'samples'    && <SamplesView />}
        {tab === 'algorithm'  && <AlgorithmView />}
        {tab === 'settings'   && <SettingsView />}
      </div>
    </div>
  );
}

function TabStrip({ current, onChange }: { current: TabKey; onChange: (t: TabKey) => void }) {
  const tabs: Array<{ key: TabKey; label: string; sub: string }> = [
    { key: 'challenges', label: 'BSL Challenges',     sub: 'Fabric corrections' },
    { key: 'batches',    label: 'Filing Batches',     sub: 'BDC bulk upload' },
    { key: 'samples',    label: 'Compliance Samples', sub: 'Quarterly draws' },
    { key: 'algorithm',  label: 'Methodology',        sub: 'Audit reference' },
    { key: 'settings',   label: 'Settings',           sub: 'Provider identity' },
  ];
  return (
    <div style={{ borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 0 }}>
      {tabs.map(t => {
        const active = current === t.key;
        return (
          <button key={t.key}
            onClick={() => onChange(t.key)}
            style={{
              padding: '14px 24px',
              background: 'transparent', border: 0,
              borderBottom: active ? `2px solid ${C.accent}` : '2px solid transparent',
              cursor: 'pointer', textAlign: 'left',
              marginBottom: -1,
            }}>
            <div style={{ fontSize: 13, fontWeight: 600,
                          color: active ? C.accent : C.text }}>
              {t.label}
            </div>
            <div style={{ fontSize: 10, color: C.textFaint, marginTop: 2,
                          textTransform: 'uppercase', letterSpacing: '.04em' }}>
              {t.sub}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── Cards & primitives ────────────────────────────────────────────────────

function Card({ children, padding = 20, style = {} }: { children: any; padding?: number; style?: React.CSSProperties }) {
  return (
    <div style={{
      background: C.card,
      border: `1px solid ${C.border}`,
      borderRadius: 8,
      padding,
      boxShadow: C.shadow,
      ...style,
    }}>{children}</div>
  );
}

function Kpi({ label, value, sub, accent }: { label: string; value: any; sub?: string; accent?: string }) {
  return (
    <Card padding={18}>
      <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase',
                    letterSpacing: '.06em', fontWeight: 700 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: accent ?? C.text,
                    marginTop: 6, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: C.textFaint, marginTop: 4 }}>{sub}</div>}
    </Card>
  );
}

function Badge({ bg, tx, children }: { bg: string; tx: string; children: any }) {
  return (
    <span style={{
      display: 'inline-block', padding: '3px 8px', borderRadius: 4,
      background: bg, color: tx, fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '.06em',
    }}>{children}</span>
  );
}

function ServedBadge({ status }: { status: string }) {
  const c = SERVED_COLORS[status] ?? { bg: '#f3f4f6', tx: '#374151' };
  return <Badge bg={c.bg} tx={c.tx}>{status}</Badge>;
}

function StatusBadge({ status }: { status: string }) {
  const meta = STATUS_COLORS[status] ?? { bg: '#f3f4f6', tx: '#6b7280', label: status };
  return <Badge bg={meta.bg} tx={meta.tx}>{meta.label}</Badge>;
}

const th: React.CSSProperties = {
  padding: '12px 14px', fontWeight: 600, fontSize: 10,
  color: C.textMuted, textTransform: 'uppercase', letterSpacing: '.06em',
  textAlign: 'left', background: C.bg,
  borderBottom: `1px solid ${C.border}`,
};
const td: React.CSSProperties = {
  padding: '14px', verticalAlign: 'top', fontSize: 12, color: C.text,
};

const btnPrimary: React.CSSProperties = {
  padding: '7px 14px', fontSize: 11, fontWeight: 600,
  background: C.accent, color: '#fff', border: 0, borderRadius: 5,
  cursor: 'pointer', letterSpacing: '.02em',
};
const btnAccent: React.CSSProperties = {
  padding: '7px 14px', fontSize: 11, fontWeight: 600,
  background: C.green, color: '#fff', border: 0, borderRadius: 5,
  cursor: 'pointer', letterSpacing: '.02em',
};
const select: React.CSSProperties = {
  padding: '7px 10px', fontSize: 12, border: `1px solid ${C.border}`,
  borderRadius: 5, background: '#fff', color: C.text,
};

// ─── CHALLENGES VIEW ───────────────────────────────────────────────────────

function ChallengesView() {
  const qc = useQueryClient();
  const [zipFilter, setZipFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [feedback, setFeedback] = useState<Record<string, { ok: boolean; msg: string; pdfUrl?: string; jsonUrl?: string; packetId?: string }>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggle = (cid: string) => setSelected(s => { const n = new Set(s); n.has(cid) ? n.delete(cid) : n.add(cid); return n; });
  const clearSelection = () => setSelected(new Set());
  const [busy, setBusy] = useState<Record<string, 'resolving' | 'testing' | null>>({});

  const summary = useQuery<Summary>({
    queryKey: ['bead-pm', 'challenges', 'summary'],
    queryFn: () => api.get('/bead-perf-measures/challenges/summary'),
  });
  const list = useQuery<{ items: Challenge[]; count: number; nextCursor: string | null }>({
    queryKey: ['bead-pm', 'challenges', 'list', zipFilter, statusFilter],
    queryFn: () => {
      const p = new URLSearchParams({ limit: '500' });
      if (zipFilter) p.set('zip', zipFilter);
      if (statusFilter) p.set('status', statusFilter);
      return api.get(`/bead-perf-measures/challenges?${p}`);
    },
  });

  // Translate backend error JSON to a human sentence.
  function explain(err: any): string {
    const msg = err?.message ?? String(err);
    // Helper messages keyed by the {error: "..."} field from the backend
    if (msg.includes('NoTaucMatch'))           return 'No TAUC network for this customer — flagged as BYOD (customer-owned router). Ineligible for TAUC speed tests; still a valid challenge candidate on Sonar evidence alone.';
    if (msg.includes('NoCustomerNameStored'))  return 'No customer name on file for this candidate. Re-run the populate sweep.';
    if (msg.includes('TaucDetailsFailed'))     return 'TAUC accepted the name lookup but the network details call failed (usually transient rate-limit; retry in 1 min).';
    if (msg.includes('TaucUnreachable'))       return 'Could not reach TAUC OpenAPI from Build — check cert/key + OAuth credentials.';
    if (msg.includes('NoMasterDevice'))        return 'TAUC has a network for this customer but no MASTER mesh unit visible. Device may be inactive or not yet onboarded.';
    if (msg.includes('FireFailed'))            return 'TAUC rejected the speed-test trigger. If Deco: refresh the dashboard cookie. If Aginet: check device status.';
    if (msg.includes('Permission denied') || msg.includes('-80001'))
      return 'Dashboard session expired. Capture a fresh TAUC_DASHBOARD_SID + x-xsrf-token cookie pair from a logged-in browser session, then update /opt/fiberforge/.env.';
    if (msg.includes('AginetDownloadTestFailed:') || msg.includes('-70426')) return 'Aginet device already has a diagnostic task running. Wait 5-10 min for the prior task to finish, then retry.';
    if (msg.includes('-70307'))                return 'TAUC rate limit hit. Wait 60s.';
    if (msg.includes('-70401'))                return 'TAUC says this device is not an Aginet — re-run resolve-tauc to refresh category.';
    if (msg.includes('PollTimeout'))           return 'Test fired but no fresh result returned within 6 min. Device may be offline.';
    if (msg.includes('NotResolved'))           return 'Run "Resolve TAUC" first to find the device for this customer.';
    return msg;
  }

  const statusMut = useMutation({
    mutationFn: ({ cid, status }: { cid: string; status: string }) =>
      api.patch(`/bead-perf-measures/challenges/${cid}`, { status }),
    onMutate: ({ cid }) => { setBusy(b => ({ ...b, [cid]: 'resolving' })); setFeedback(f => { const n={...f}; delete n[cid]; return n; }); },
    onSettled: (_d, _e, { cid }) => { setBusy(b => ({ ...b, [cid]: null })); },
    onSuccess: (data, { cid, status }) => {
      setFeedback(f => ({ ...f, [cid]: { ok: true, msg: `Status → ${status}` } }));
      qc.invalidateQueries({ queryKey: ['bead-pm', 'challenges'] });
    },
    onError: (err: any, { cid }) => {
      setFeedback(f => ({ ...f, [cid]: { ok: false, msg: explain(err) } }));
    },
  });

  const resolveMut = useMutation({
    mutationFn: (cid: string) => api.post(`/bead-perf-measures/challenges/${cid}/resolve-tauc`, {}),
    onMutate: (cid) => { setBusy(b => ({ ...b, [cid]: 'resolving' })); setFeedback(f => { const n={...f}; delete n[cid]; return n; }); },
    onSettled: (_data, _err, cid) => { setBusy(b => ({ ...b, [cid]: null })); },
    onSuccess: (data, cid) => {
      setFeedback(f => ({ ...f, [cid]: { ok: true,
        msg: `Resolved → ${data?.tauc_device_category ?? 'TAUC'} · ${data?.tauc_device_model ?? data?.tauc_device_id?.slice(0,16) ?? ''}` } }));
      qc.invalidateQueries({ queryKey: ['bead-pm', 'challenges'] });
    },
    onError: (err: any, cid) => {
      setFeedback(f => ({ ...f, [cid]: { ok: false, msg: explain(err) } }));
    },
  });
  const testMut = useMutation({
    mutationFn: (cid: string) => api.post(`/bead-perf-measures/challenges/${cid}/test`, {}),
    onMutate: (cid) => { setBusy(b => ({ ...b, [cid]: 'testing' })); setFeedback(f => { const n={...f}; delete n[cid]; return n; }); },
    onSettled: (_data, _err, cid) => { setBusy(b => ({ ...b, [cid]: null })); },
    onSuccess: (data, cid) => {
      const dl = data?.download_mbps ? `${Math.round(data.download_mbps)} Mbps down` : 'test fired';
      const ul = data?.upload_mbps ? ` / ${Math.round(data.upload_mbps)} up` : '';
      const lat = data?.latency_ms ? ` · ${data.latency_ms} ms` : '';
      setFeedback(f => ({ ...f, [cid]: { ok: true, msg: `Test complete — ${dl}${ul}${lat}` } }));
      qc.invalidateQueries({ queryKey: ['bead-pm', 'challenges'] });
    },
    onError: (err: any, cid) => {
      setFeedback(f => ({ ...f, [cid]: { ok: false, msg: explain(err) } }));
    },
  });
  const packetMut = useMutation<any, any, string>({
    mutationFn: (cid: string) => api.post(`/bead-perf-measures/challenges/${cid}/generate-packet`, {}),
    onMutate: (cid) => { setBusy(b => ({ ...b, [cid]: 'testing' })); setFeedback(f => { const n={...f}; delete n[cid]; return n; }); },
    onSettled: (_d, _e, cid) => { setBusy(b => ({ ...b, [cid]: null })); },
    onSuccess: (data, cid) => {
      const sz  = data?.pdf_byte_size ? ` · ${(data.pdf_byte_size/1024).toFixed(1)} KB` : '';
      const sha = data?.sha256_json ? ` · sha256 ${data.sha256_json.slice(0, 10)}…` : '';
      setFeedback(f => ({ ...f, [cid]: {
        ok: true,
        msg: `Packet v${data?.version} generated${sz}${sha}`,
        pdfUrl:   data?.pdf_url,
        jsonUrl:  data?.json_url,
        packetId: data?.packet_id,
      } }));
      qc.invalidateQueries({ queryKey: ['bead-pm', 'challenges'] });
    },
    onError: (err: any, cid) => {
      setFeedback(f => ({ ...f, [cid]: { ok: false, msg: explain(err) } }));
    },
  });

  const bulkMut = useMutation<any, any, { candidate_ids: string[]; patch: any; label: string }>({
    mutationFn: (vars) => api.post('/bead-perf-measures/challenges/bulk-update',
      { candidate_ids: vars.candidate_ids, patch: vars.patch }),
    onSuccess: (data, vars) => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['bead-pm', 'challenges'] });
      alert(`✓ ${vars.label}: ${data?.updated} row(s) updated`);
    },
    onError: (err: any) => alert(`Bulk update failed: ${err?.message ?? err}`),
  });

  const createBatchMut = useMutation<any, any, void>({
    mutationFn: () => api.post('/bead-perf-measures/filing-batches', {
      candidate_ids: Array.from(selected),
      filing_type:   'crowdsource',
      methodology:   'Knowledge of Infrastructure',
      category_code: 10,
    }),
    onSuccess: (data) => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['bead-pm', 'challenges'] });
      qc.invalidateQueries({ queryKey: ['bead-pm', 'filing-batches'] });
      alert(`✓ Batch "${data?.name}" created with ${data?.member_count ?? 0} members`);
    },
    onError: (err: any) => alert(`Create batch failed: ${err?.message ?? err}`),
  });

  const s = summary.data;
  const items = list.data?.items ?? [];

  const uniqueBsls = useMemo(() =>
    s?.by_served_status.reduce((a, b) => a + parseInt(b.unique_bsls, 10), 0) ?? 0,
    [s]);

  const taucResolved = useMemo(() =>
    s?.by_tauc_category.reduce((a, b) => a + parseInt(b.n, 10), 0) ?? 0,
    [s]);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 14, marginTop: 24, marginBottom: 24 }}>
        <Kpi label="Unique BSLs in challenge pool"
             value={uniqueBsls}
             sub={`${s?.total ?? 0} customer matches across ${s?.by_zip.length ?? 0} ZIPs`}
             accent={C.accent} />
        <Kpi label="Active subscribers"
             value={s?.by_status.find(x => x.status === 'new')?.n ?? '0'}
             sub="At FCC-unserved/underserved BSLs" />
        <Kpi label="Tests fired"
             value={s?.tested_count ?? 0}
             sub="Against challenge candidates"
             accent={(s?.tested_count ?? 0) > 0 ? C.green : undefined} />
        <Kpi label="Speed-test eligible"
             value={taucResolved}
             sub={s?.by_tauc_category.length
                ? s.by_tauc_category.map(c => `${c.tauc_device_category}: ${c.n}`).join(' · ')
                : 'No TAUC-managed CPE linked yet'} />
      </div>

      <Card padding={0} style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px',
                      borderBottom: `1px solid ${C.border}`, background: C.card, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, flex: 1, minWidth: 200 }}>
            Challenge candidates
            <span style={{ marginLeft: 8, fontSize: 11, color: C.textMuted, fontWeight: 400 }}>
              · {items.length} showing
            </span>
          </div>
          <span style={{ fontSize: 11, color: C.textMuted }}>Filter:</span>
          <select value={zipFilter} onChange={e => setZipFilter(e.target.value)} style={select}>
            <option value="">All ZIPs</option>
            {s?.by_zip.map(z => (
              <option key={z.core_zip} value={z.core_zip}>
                {z.core_zip} — {z.n} matches, {z.unique_bsls} BSLs
              </option>
            ))}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={select}>
            <option value="">All statuses</option>
            {Object.entries(STATUS_COLORS).map(([k, v]) =>
              <option key={k} value={k}>{v.label}</option>)}
          </select>
          <a href={`/api/bead-perf-measures/challenges/export.csv${
            (zipFilter || statusFilter) ?
              '?' + new URLSearchParams({ ...(zipFilter && {zip: zipFilter}), ...(statusFilter && {status: statusFilter}) })
              : ''
          }`}
             style={{ ...btnPrimary, textDecoration: 'none', display: 'inline-flex',
                      alignItems: 'center', gap: 6 }}
             title="Download evidence bundle as CSV (FCC challenge-ready)">
            ⬇ Export CSV
          </a>
        </div>

        {selected.size > 0 && (
          <div style={{
            marginBottom: 12, padding: '10px 14px',
            background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 6,
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1e3a8a' }}>
              {selected.size} selected
            </span>
            <span style={{ fontSize: 11, color: '#475569' }}>Apply to all:</span>
            <select onChange={(e) => {
              if (!e.target.value) return;
              bulkMut.mutate({ candidate_ids: Array.from(selected),
                patch: { technology: parseInt(e.target.value, 10) },
                label: `Tech → ${e.target.options[e.target.selectedIndex].text}` });
              e.target.value = '';
            }} style={bulkSelectStyle} defaultValue="">
              <option value="" disabled>Set technology…</option>
              <option value="50">50 — Fiber (FTTP)</option>
              <option value="70">70 — Unlicensed Fixed Wireless</option>
              <option value="71">71 — Licensed Fixed Wireless</option>
              <option value="72">72 — Licensed-by-Rule FWA</option>
              <option value="40">40 — HFC / Cable</option>
              <option value="10">10 — Copper</option>
              <option value="0">0 — Other</option>
            </select>
            <select onChange={(e) => {
              if (!e.target.value) return;
              bulkMut.mutate({ candidate_ids: Array.from(selected),
                patch: { bizrescode: e.target.value as 'R'|'B'|'X' },
                label: `BizRes → ${e.target.value}` });
              e.target.value = '';
            }} style={bulkSelectStyle} defaultValue="">
              <option value="" disabled>Set type…</option>
              <option value="R">R — Residential</option>
              <option value="B">B — Business</option>
              <option value="X">X — Both</option>
            </select>
            <button onClick={() => createBatchMut.mutate()}
                    disabled={createBatchMut.isPending}
                    style={{ ...bulkBtnStyle, background: '#1e3a8a', color: 'white' }}>
              {createBatchMut.isPending ? 'Creating…' : `📤 New batch from ${selected.size}`}
            </button>
            <button onClick={clearSelection} style={bulkBtnStyle}>Clear</button>
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...th, width: 28 }}>
                  <input type="checkbox"
                    checked={items.length > 0 && items.every(c => selected.has(c.id))}
                    onChange={e => {
                      if (e.target.checked) setSelected(new Set(items.map(c => c.id)));
                      else setSelected(new Set());
                    }} />
                </th>
                <th style={th}>Customer</th>
                <th style={th}>SecureNet address</th>
                <th style={th}>FCC fabric</th>
                <th style={th}>FCC speed</th>
                <th style={th}>TAUC device</th>
                <th style={th}>Status</th>
                <th style={th}>Tests</th>
                <th style={{ ...th, width: 160 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c, i) => (
                <tr key={c.id} style={{
                  background: selected.has(c.id) ? '#eff6ff' : (i % 2 === 0 ? C.card : C.bg),
                  borderBottom: `1px solid ${C.border}`,
                }}>
                  <td style={{ ...td, width: 28 }}>
                    <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                  </td>
                  <td style={td}>
                    {c.core_customer_name ? (
                      <>
                        <div style={{ fontWeight: 600 }}>{c.core_customer_name}</div>
                        <div style={{ fontSize: 10, color: C.textFaint, marginTop: 2 }}>
                          {c.core_account_status ?? 'Unknown status'}
                          {c.core_monthly_total ? ` · $${c.core_monthly_total}/mo` : ''}
                        </div>
                        {c.core_customer_id && (
                          <div style={{ fontSize: 10, color: C.textFaint, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
                            {c.core_customer_id}
                          </div>
                        )}
                      </>
                    ) : (
                      <span style={{ color: C.textFaint, fontStyle: 'italic' }}>
                        {c.core_customer_id ?? '(unknown)'}
                      </span>
                    )}
                  </td>
                  <td style={td}>
                    <div>{c.core_address}</div>
                    {c.core_unit && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{c.core_unit}</div>}
                    <div style={{ fontSize: 10, color: C.textFaint, marginTop: 2 }}>
                      {c.core_zip}
                      {c.core_on_net && (
                        <span style={{ marginLeft: 8, color: C.green, fontWeight: 600 }}>· on-net</span>
                      )}
                    </div>
                  </td>
                  <td style={td}>
                    <div>{c.fcc_address}</div>
                    <div style={{ fontSize: 10, color: C.textFaint, marginTop: 2,
                                  fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
                      FCC {c.fcc_location_id}
                    </div>
                  </td>
                  <td style={td}>
                    <ServedBadge status={c.fcc_served_status} />
                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                      {c.fcc_dl_mbps}/{c.fcc_ul_mbps} Mbps
                    </div>
                  </td>
                  <td style={td}>
                    {c.cpe_status === 'tauc_managed' && c.tauc_device_id ? (
                      <>
                        <div style={{ fontWeight: 600 }}>
                          {c.tauc_device_category}
                          {c.tauc_device_model && <span style={{ color: C.textMuted, fontWeight: 400 }}> · {c.tauc_device_model}</span>}
                        </div>
                        <div style={{ fontSize: 10, color: C.textFaint, fontFamily: 'ui-monospace, SFMono-Regular, monospace', marginTop: 2 }}>
                          {c.tauc_device_id.slice(0, 22)}…
                        </div>
                      </>
                    ) : c.cpe_status === 'byod' ? (
                      <>
                        <Badge bg="#f3e8ff" tx="#6b21a8">BYOD</Badge>
                        <div style={{ fontSize: 10, color: C.textFaint, marginTop: 4, lineHeight: 1.3 }}>
                          Customer-owned router. Ineligible for TAUC speed test.
                        </div>
                      </>
                    ) : c.cpe_status === 'error' ? (
                      <span style={{ fontSize: 11, color: C.amber, fontStyle: 'italic' }}>
                        link error — retry
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: C.textFaint, fontStyle: 'italic' }}>
                        not resolved
                      </span>
                    )}
                  </td>
                  <td style={td}><StatusBadge status={c.status} /></td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{c.test_count}</div>
                    {c.latest_test_at && (
                      <div style={{ fontSize: 10, color: C.textFaint, marginTop: 2 }}>
                        {new Date(c.latest_test_at).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td style={td}>
                    <ActionCell candidate={c}
                                busy={busy[c.id]}
                                onStatus={(status) => statusMut.mutate({ cid: c.id, status })}
                                onResolve={() => resolveMut.mutate(c.id)}
                                onFire={() => testMut.mutate(c.id)}
                                onGeneratePacket={() => packetMut.mutate(c.id)} />
                    {feedback[c.id] && (
                      <div style={{
                        marginTop: 6, fontSize: 10, lineHeight: 1.4,
                        padding: '6px 9px', borderRadius: 4,
                        background: feedback[c.id].ok ? '#d1fae5' : '#fee2e2',
                        color:      feedback[c.id].ok ? '#065f46' : '#991b1b',
                        maxWidth: 220,
                      }}>
                        <div>{feedback[c.id].msg}</div>
                        {feedback[c.id].pdfUrl && (
                          <div style={{ marginTop: 4, display: 'flex', gap: 10 }}>
                            <a href={feedback[c.id].pdfUrl} target="_blank" rel="noreferrer"
                               style={{ color: '#065f46', fontWeight: 600, textDecoration: 'underline' }}>
                              \ud83d\udcc4 PDF
                            </a>
                            {feedback[c.id].jsonUrl && (
                              <a href={feedback[c.id].jsonUrl} target="_blank" rel="noreferrer"
                                 style={{ color: '#065f46', fontWeight: 600, textDecoration: 'underline' }}>
                                \ud83d\udccb JSON
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: C.textFaint }}>
                  No candidates match the current filter.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// Per-row action cell. Primary: workflow status transition. Secondary: TAUC.
function ActionCell({ candidate, busy, onStatus, onResolve, onFire, onGeneratePacket }: {
  candidate: Challenge;
  busy: 'resolving' | 'testing' | null | undefined;
  onStatus: (s: string) => void;
  onResolve: () => void;
  onFire: () => void;
  onGeneratePacket: () => void;
}) {
  // Next workflow step per current status. After 'submitted' the action splits.
  const next = NEXT_STATUS[candidate.status];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
      {/* Primary: status workflow */}
      {Array.isArray(next) ? (
        <div style={{ display: 'flex', gap: 4 }}>
          {next.map(n => (
            <button key={n.status} onClick={() => onStatus(n.status)} style={n.style}>
              {n.label}
            </button>
          ))}
        </div>
      ) : next ? (
        <button
          onClick={() => next.status === 'ready_to_submit' ? onGeneratePacket() : onStatus(next.status)}
          style={next.style}
          title={next.status === 'ready_to_submit'
            ? 'Generate FCC challenge evidence packet (PDF + JSON, SHA-256 stamped) and mark this candidate ready to submit.'
            : undefined}>
          {next.status === 'ready_to_submit' ? '\ud83d\udcc4 Generate packet' : next.label}
        </button>
      ) : (
        <div style={{ fontSize: 10, color: C.textFaint, fontStyle: 'italic',
                      padding: '4px 0' }}>
          Workflow complete
        </div>
      )}

      {/* Secondary: TAUC speed-test evidence (optional, hidden for BYOD) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 4,
                    borderTop: `1px solid ${C.border}` }}>
        <span style={{ fontSize: 9, color: C.textFaint, textTransform: 'uppercase',
                       letterSpacing: '.05em', fontWeight: 600 }}>
          Speed test:
        </span>
        {candidate.cpe_status === 'tauc_managed' && candidate.tauc_device_id ? (
          <button onClick={onFire} disabled={busy === 'testing'} style={btnSecondary}>
            {busy === 'testing' ? 'Testing…' : 'Run test'}
          </button>
        ) : candidate.cpe_status === 'byod' ? (
          <span style={{ fontSize: 10, color: '#6b21a8', fontWeight: 600 }}>
            n/a · BYOD
          </span>
        ) : (
          <button onClick={onResolve} disabled={busy === 'resolving'} style={btnSecondary}>
            {busy === 'resolving' ? 'Linking…' : 'Link TAUC'}
          </button>
        )}
      </div>
    </div>
  );
}

// Status → next-step workflow definition.
type NextStep = { status: string; label: string; style: React.CSSProperties };
const NEXT_STATUS: Record<string, NextStep | NextStep[] | null> = {
  new:              { status: 'reviewed',         label: '✓ Mark reviewed',  style: btnAccent },
  reviewed:         { status: 'ready_to_submit',  label: '→ Mark ready',     style: btnAccent },
  ready_to_submit:  { status: 'submitted',        label: '⤴ Mark submitted', style: btnAccent },
  submitted:        [
    { status: 'accepted', label: '✓ Accepted',    style: { ...btnAccent, background: C.green } },
    { status: 'rejected', label: '✗ Rejected',    style: { ...btnAccent, background: C.red } },
  ],
  accepted:         null,
  rejected:         null,
  duplicate:        null,
};

const btnSecondary: React.CSSProperties = {
  padding: '4px 10px', fontSize: 10, fontWeight: 500,
  background: '#fff', color: C.text, border: `1px solid ${C.borderHover}`,
  borderRadius: 4, cursor: 'pointer',
};

// ─── SAMPLES VIEW ──────────────────────────────────────────────────────────

function SamplesView() {
  const qc = useQueryClient();
  const [projectId, setProjectId] = useState('');
  const [quarter, setQuarter] = useState(defaultQuarter());

  const projects = useQuery<{ items: Project[] }>({
    queryKey: ['bead-pm', 'projects'],
    queryFn: () => api.get('/bead-perf-measures/projects'),
  });
  const samples = useQuery<{ items: Sample[] }>({
    queryKey: ['bead-pm', 'samples'],
    queryFn: () => api.get('/bead-perf-measures/sample'),
  });

  const drawMut = useMutation({
    mutationFn: (body: { project_id: string; quarter: string; force?: boolean }) =>
      api.post('/bead-perf-measures/sample', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bead-pm', 'samples'] }),
  });

  const selected = useMemo(
    () => projects.data?.items.find(p => p.id === projectId),
    [projects.data, projectId]
  );

  const targetSize = useMemo(() => {
    if (!selected) return 0;
    const n = parseInt(selected.awarded_count, 10) || 0;
    if (n <= 0) return 0;
    if (n <= 50) return Math.min(5, n);
    if (n <= 500) return Math.max(1, Math.ceil(n * 0.10));
    return 50;
  }, [selected]);

  return (
    <div style={{ marginTop: 24 }}>
      {/* Draw a new sample — with live project stats */}
      <Card padding={0} style={{ overflow: 'hidden', marginBottom: 24 }}>
        <div style={{ padding: 20, borderBottom: `1px solid ${C.border}`,
                      display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
            Draw a new quarterly sample
          </div>
          <div style={{ fontSize: 11, color: C.textMuted }}>
            NTIA-mandated sizing · stratified random · audit-reproducible seed
          </div>
        </div>
        <div style={{ padding: 20, display: 'grid',
                      gridTemplateColumns: '320px 110px 1fr auto', gap: 14, alignItems: 'flex-end' }}>
          <div>
            <label style={lblStyle}>BEAD project</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)}
                    style={{ ...select, width: '100%' }}>
              <option value="">— pick a project —</option>
              {projects.data?.items.map(p => (
                <option key={p.id} value={p.id}>
                  {p.project_code} · {p.project_name} ({p.county})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={lblStyle}>Quarter</label>
            <input value={quarter} onChange={e => setQuarter(e.target.value)}
                   placeholder="2026Q2"
                   style={{ ...select, width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }} />
          </div>
          <div style={{ alignSelf: 'stretch', display: 'flex', alignItems: 'center' }}>
            {selected ? (
              <div style={{ padding: '10px 14px', background: C.bg, borderRadius: 6,
                            border: `1px solid ${C.border}`, fontSize: 11, lineHeight: 1.7, width: '100%' }}>
                <span style={{ color: C.textMuted }}>Population:</span>{' '}
                <strong>{parseInt(selected.awarded_count).toLocaleString()}</strong>
                <span style={{ color: C.textMuted }}> awarded BSLs · </span>
                <strong style={{ color: SERVED_COLORS.unserved.tx }}>
                  {selected.unserved_count}
                </strong>{' '}
                <span style={{ color: C.textMuted }}>unserved · </span>
                <strong style={{ color: SERVED_COLORS.underserved.tx }}>
                  {selected.underserved_count}
                </strong>{' '}
                <span style={{ color: C.textMuted }}>underserved</span>
                <br />
                <span style={{ color: C.textMuted }}>NTIA sample target:</span>{' '}
                <strong style={{ color: C.accent }}>{targetSize}</strong>{' '}
                <span style={{ color: C.textMuted }}>BSLs ({Math.round(targetSize / parseInt(selected.awarded_count) * 1000) / 10}%)</span>
              </div>
            ) : (
              <div style={{ fontSize: 11, color: C.textFaint, fontStyle: 'italic' }}>
                Pick a project to see population stats
              </div>
            )}
          </div>
          <button onClick={() => drawMut.mutate({ project_id: projectId, quarter })}
                  disabled={!projectId || drawMut.isPending}
                  style={btnPrimary}>
            {drawMut.isPending ? 'Drawing…' : 'Draw sample'}
          </button>
        </div>
        {drawMut.isError && (
          <div style={{ padding: '10px 20px', borderTop: `1px solid ${C.border}`,
                        background: '#fee2e2', color: '#991b1b', fontSize: 11 }}>
            {(drawMut.error as any)?.message ?? 'Draw failed'}
          </div>
        )}
      </Card>

      {/* Existing samples */}
      <Card padding={0} style={{ overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Existing samples</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Quarter</th>
                <th style={th}>Project</th>
                <th style={th}>Population</th>
                <th style={th}>Sample drawn</th>
                <th style={th}>Tested</th>
                <th style={th}>Status</th>
                <th style={th}>Drawn</th>
                <th style={th}>Seed</th>
              </tr>
            </thead>
            <tbody>
              {samples.data?.items.map((s, i) => (
                <tr key={s.id} style={{
                  background: i % 2 === 0 ? C.card : C.bg,
                  borderBottom: `1px solid ${C.border}`,
                }}>
                  <td style={td}><strong>{s.quarter}</strong></td>
                  <td style={td}>
                    <div>{s.project_name}</div>
                    <div style={{ fontSize: 10, color: C.textFaint }}>{s.project_code}</div>
                  </td>
                  <td style={td}>{s.population_size.toLocaleString()}</td>
                  <td style={td}>
                    <strong>{s.member_count}</strong>
                    <span style={{ color: C.textMuted }}> / {s.sample_size} target</span>
                  </td>
                  <td style={td}>
                    {s.tested_member_count} / {s.member_count}
                    {parseInt(s.tested_member_count, 10) > 0 && (
                      <span style={{ color: C.green, marginLeft: 6, fontSize: 10 }}>
                        ({Math.round(parseInt(s.tested_member_count, 10) / parseInt(s.member_count, 10) * 100)}%)
                      </span>
                    )}
                  </td>
                  <td style={td}>
                    <Badge bg={s.status === 'active' ? '#dbeafe' : '#f3f4f6'}
                           tx={s.status === 'active' ? C.accent : '#6b7280'}>
                      {s.status}
                    </Badge>
                  </td>
                  <td style={td}>{new Date(s.drawn_at).toLocaleDateString()}</td>
                  <td style={{ ...td, fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                              fontSize: 10, color: C.textFaint }}>
                    {s.seed.slice(0, 20)}…
                  </td>
                </tr>
              ))}
              {(!samples.data?.items.length) && (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: C.textFaint }}>
                  No samples drawn yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

const lblStyle: React.CSSProperties = {
  display: 'block', fontSize: 10, color: C.textMuted, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 5,
};

// ─── ALGORITHM / METHODOLOGY VIEW ─────────────────────────────────────────

function AlgorithmView() {
  return (
    <div style={{ marginTop: 24, display: 'grid',
                  gridTemplateColumns: '1fr 380px', gap: 20 }}>
      <div>
        <Card>
          <SectionHeader
            title="Random Sampling Methodology"
            sub="Stratified random sampling without replacement · deterministic seed · single SQL transaction" />

          <Subsection title="Statutory citation">
            <p style={paragStyle}>
              The sampling rules below implement the requirements of the
              {' '}<a href="https://broadbandusa.ntia.gov/sites/default/files/2025-09/Performance_Measures_Policy_Notice.pdf"
                     target="_blank" rel="noreferrer"
                     style={{ color: C.accent }}>
                NTIA Performance Measures Policy Notice
              </a>{' '}
              (Broadband Equity, Access and Deployment Program, September 2025;
              OMB control no. 0660-NEW).
            </p>
            <p style={paragStyle}>
              For each BEAD-funded project, every quarterly reporting period a
              random sample of awarded Broadband Serviceable Locations (BSLs)
              is selected and tested. The sample must be reproducible from an
              auditor-supplied seed and stratified across served-status so the
              tested sub-population mirrors the eligible universe.
            </p>
          </Subsection>

          <Subsection title="Sample-size schedule (NTIA-mandated)">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
              <thead>
                <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                  <th style={{ ...th, background: 'transparent' }}>Population (awarded BSLs)</th>
                  <th style={{ ...th, background: 'transparent' }}>Sample target</th>
                  <th style={{ ...th, background: 'transparent' }}>Coverage</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={td}>≤ 50</td>
                  <td style={td}><code>min(5, N)</code></td>
                  <td style={td}>5–100%</td>
                </tr>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={td}>51 – 500</td>
                  <td style={td}><code>⌈N × 0.10⌉</code></td>
                  <td style={td}>10%</td>
                </tr>
                <tr>
                  <td style={td}>≥ 501</td>
                  <td style={td}><code>50</code></td>
                  <td style={td}>10% → ~1% (asymptotic)</td>
                </tr>
              </tbody>
            </table>
          </Subsection>

          <Subsection title="Stratification">
            <p style={paragStyle}>
              Within the awarded BSLs for a given project, the population is
              partitioned by <code>served_status</code> (one of
              {' '}<code>unserved</code>, <code>underserved</code>, <code>served</code>,
              {' '}<code>unknown</code>). Each stratum receives a proportional
              share of the sample, rounded up via
              {' '}<code>CEIL(strata_count × target_sample_size / population)</code>.
              <strong> GREATEST(1, …)</strong> guarantees that any represented stratum
              gets at least one BSL — so a 50-of-961 sample of a population
              that is 100% unserved will return 50 unserved BSLs, while a
              mixed population produces a sample that mirrors the stratum
              ratios within ±1 BSL per stratum.
            </p>
          </Subsection>

          <Subsection title="Reproducibility — seed mechanism">
            <p style={paragStyle}>
              Within a single PostgreSQL transaction, the picker calls
              {' '}<code>setseed(x)</code> to bind subsequent <code>random()</code>{' '}
              outputs deterministically. The seed input is a hash of
              <code> quarter ‖ project_id ‖ optional_user_seed</code>, mapped
              into <code>[-1, 1]</code> per PostgreSQL's
              {' '}<code>setseed</code>{' '}contract.
            </p>
            <p style={paragStyle}>
              The exact seed string and the per-row <code>rng_value</code>{' '}
              produced by <code>random()</code> are persisted to
              {' '}<code>bead_pm_sample</code> and{' '}
              <code>bead_pm_sample_member</code>. Re-running the picker with
              the same seed against an unchanged population produces an
              identical sample, byte-for-byte.
            </p>
          </Subsection>

          <Subsection title="Exact SQL of the draw (verbatim)">
            <pre style={preStyle}>{`-- Inside a single transaction:
SELECT setseed($seed);                     -- bind random()

INSERT INTO bead_pm_sample_member (sample_id, bead_location_id, stratum_key, rng_value)
WITH strata AS (
  SELECT id,
         COALESCE(served_status,'unknown') AS served_status,
         random() AS r,
         COUNT(*) OVER (PARTITION BY COALESCE(served_status,'unknown')) AS strata_n,
         COUNT(*) OVER () AS total_n
    FROM bead_location
   WHERE project_id = $project_id AND awarded = TRUE
),
ranked AS (
  SELECT id, served_status, r,
         ROW_NUMBER() OVER (PARTITION BY served_status ORDER BY r) AS rn,
         GREATEST(1, CEIL(strata_n::float * $target / total_n))::int AS take_n
    FROM strata
)
SELECT $sample_id, id, 'served_status='||served_status, r
  FROM ranked
 WHERE rn <= take_n
 ORDER BY served_status, r;`}</pre>
          </Subsection>

          <Subsection title="Audit trail per sample">
            <ul style={listStyle}>
              <li><code>drawn_at</code> — timestamptz</li>
              <li><code>drawn_by</code> — UUID of the user who triggered the draw</li>
              <li><code>seed</code> — exact text input to the hash, persisted verbatim</li>
              <li><code>population_size</code> — count of awarded BSLs at draw time</li>
              <li><code>sample_size</code> — NTIA target</li>
              <li><code>selection_method</code> — always <code>stratified_random</code> for v1</li>
              <li>Per member: <code>rng_value</code>, <code>stratum_key</code></li>
              <li>Old samples for the same quarter are superseded (not deleted) — full
                replay history retained.</li>
            </ul>
          </Subsection>
        </Card>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted,
                        textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Verification protocol
          </div>
          <ol style={{ ...listStyle, paddingLeft: 18 }}>
            <li>Auditor receives sample row from
                <code> bead_pm_sample</code> including the persisted <code>seed</code>.</li>
            <li>Auditor re-executes the SQL block (left) with the persisted
                <code> seed</code> and <code>target</code>.</li>
            <li>Output row-set hash matches the stored member list, byte-for-byte.</li>
            <li>χ² test on stratum representation vs population proves
                proportional allocation within tolerance.</li>
            <li>Re-running with a different seed produces a different sample —
                proves the picker is not deterministic on inputs alone, only
                on (inputs + seed).</li>
          </ol>
        </Card>

        <Card>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted,
                        textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Why this passes audit
          </div>
          <ul style={{ ...listStyle, paddingLeft: 18 }}>
            <li><strong>Determinism:</strong> exact replay from seed string</li>
            <li><strong>Single transaction:</strong> no partial draws</li>
            <li><strong>Proportional stratification:</strong> sample reflects population mix</li>
            <li><strong>Idempotency:</strong> same (project, quarter) returns 409 unless explicitly superseded</li>
            <li><strong>Full audit trail:</strong> every input + every random number persisted</li>
            <li><strong>Open SQL:</strong> auditor can re-execute the literal query</li>
          </ul>
        </Card>

        <Card>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted,
                        textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Source-of-truth refs
          </div>
          <div style={{ fontSize: 11, color: C.text, lineHeight: 1.8 }}>
            <div>Migration: <code>bead_pm_sample</code>, <code>bead_pm_sample_member</code></div>
            <div>Picker: <code>routes/bead-perf-measures.ts</code> · POST /sample</div>
            <div>Universe: <code>bead_location WHERE awarded = TRUE</code></div>
            <div>Test ingest: <code>bead_speed_test.test_run_id = 'sample:&lt;member_id&gt;'</code></div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function SectionHeader({ title, sub }: { title: string; sub?: string }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{title}</div>
      {sub && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Subsection({ title, children }: { title: string; children: any }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted,
                    textTransform: 'uppercase', letterSpacing: '.06em',
                    paddingBottom: 8, borderBottom: `1px solid ${C.border}`, marginBottom: 12 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

const paragStyle: React.CSSProperties = {
  fontSize: 12.5, color: C.text, lineHeight: 1.65, marginBottom: 10,
};
const listStyle: React.CSSProperties = {
  fontSize: 12, color: C.text, lineHeight: 1.7, paddingLeft: 18, margin: 0,
};
const preStyle: React.CSSProperties = {
  background: C.bg, border: `1px solid ${C.border}`, borderRadius: 5,
  padding: 14, fontSize: 11, color: C.text,
  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
  overflowX: 'auto', lineHeight: 1.55,
};

const bulkSelectStyle: React.CSSProperties = {
  padding: '4px 8px', fontSize: 11, fontWeight: 500,
  background: 'white', color: '#0f172a',
  border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer',
};
const bulkBtnStyle: React.CSSProperties = {
  padding: '5px 10px', fontSize: 11, fontWeight: 600,
  background: 'white', color: '#1e3a8a',
  border: '1px solid #93c5fd', borderRadius: 4, cursor: 'pointer',
};

// ─── Filing Batches view ──────────────────────────────────────────────────

function FilingBatchesView() {
  const qc = useQueryClient();
  const batches = useQuery<{ items: FilingBatch[] }>({
    queryKey: ['bead-pm', 'filing-batches'],
    queryFn: () => api.get('/bead-perf-measures/filing-batches'),
  });

  const buildMut = useMutation<any, any, string>({
    mutationFn: (bid) => api.post(`/bead-perf-measures/filing-batches/${bid}/build`, {}),
    onSuccess: (data, _bid) => {
      qc.invalidateQueries({ queryKey: ['bead-pm', 'filing-batches'] });
      const probs = data?.validation_problems;
      const warn = probs && probs.length > 0
        ? `\n\n⚠️ Validation problems:\n - ${probs.join('\n - ')}`
        : '';
      alert(`✓ Built: ${data?.member_count} rows · CSV ${data?.csv_bytes}B · ZIP ${data?.zip_bytes}B${warn}`);
    },
    onError: (err: any) => alert(`Build failed: ${err?.message ?? err}`),
  });

  const submitMut = useMutation<any, any, { id: string; confirmation: string }>({
    mutationFn: (v) => api.patch(`/bead-perf-measures/filing-batches/${v.id}`,
      { status: 'submitted', submitted_confirmation: v.confirmation }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bead-pm', 'filing-batches'] }),
  });

  const deleteMut = useMutation<any, any, string>({
    mutationFn: (bid) => api.del(`/bead-perf-measures/filing-batches/${bid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bead-pm', 'filing-batches'] }),
    onError: (err: any) => alert(`Delete failed: ${err?.message ?? err}`),
  });

  const items = batches.data?.items ?? [];

  return (
    <div style={{ marginTop: 24 }}>
      <Card padding={20} style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 6 }}>
          BDC filing batches
        </div>
        <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.6 }}>
          Each batch produces a BDC-spec CSV plus a ZIP bundle containing per-row evidence packets,
          ready for upload to the FCC Broadband Data Collection portal. Default filing type is
          <strong> bulk crowdsource </strong> with <strong>category_code 10 (Missing Provider)</strong> —
          the right path for "we serve a customer the FCC fabric shows as unserved."
        </div>
      </Card>

      <Card padding={0} style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Batch</th>
                <th style={th}>Type</th>
                <th style={th}>Vintage</th>
                <th style={th}>Members</th>
                <th style={th}>Status</th>
                <th style={th}>Built</th>
                <th style={{ ...th, width: 360 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b, i) => (
                <tr key={b.id} style={{
                  background: i % 2 === 0 ? C.card : C.bg,
                  borderBottom: `1px solid ${C.border}`,
                }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{b.name}</div>
                    <div style={{ fontSize: 10, color: C.textFaint, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
                      {b.id.slice(0, 8)}…
                    </div>
                  </td>
                  <td style={td}>{b.filing_type}</td>
                  <td style={td}>{String(b.data_vintage).slice(0, 10)}</td>
                  <td style={td}>{b.member_count}</td>
                  <td style={td}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '.05em',
                      background: b.status === 'submitted' ? '#d1fae5'
                                : b.status === 'built'     ? '#dbeafe'
                                : b.status === 'draft'     ? '#f3f4f6'
                                : '#fee2e2',
                      color:      b.status === 'submitted' ? '#065f46'
                                : b.status === 'built'     ? '#1e3a8a'
                                : b.status === 'draft'     ? '#475569'
                                : '#991b1b',
                    }}>{b.status}</span>
                  </td>
                  <td style={td}>
                    {b.built_at
                      ? <span style={{ fontSize: 10, color: C.textMuted }}>
                          {new Date(b.built_at).toLocaleString()}<br />
                          {b.built_member_count} rows
                        </span>
                      : <span style={{ fontSize: 10, color: C.textFaint }}>—</span>}
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {b.status === 'draft' && (
                        <button onClick={() => buildMut.mutate(b.id)}
                                disabled={buildMut.isPending}
                                style={{ ...bulkBtnStyle, background: '#1e3a8a', color: 'white' }}>
                          {buildMut.isPending ? 'Building…' : '⚙ Build'}
                        </button>
                      )}
                      {b.built_at && (
                        <>
                          <a href={`/api/bead-perf-measures/filing-batches/${b.id}/download.zip`}
                             style={bulkBtnStyle as any}>⬇ ZIP</a>
                          <a href={`/api/bead-perf-measures/filing-batches/${b.id}/download.csv`}
                             style={bulkBtnStyle as any}>⬇ CSV</a>
                        </>
                      )}
                      {b.status === 'built' && (
                        <button onClick={() => {
                          const c = prompt('FCC BDC filing confirmation number:');
                          if (c) submitMut.mutate({ id: b.id, confirmation: c });
                        }} style={{ ...bulkBtnStyle, background: '#059669', color: 'white' }}>
                          ✓ Mark submitted
                        </button>
                      )}
                      {(b.status === 'draft' || b.status === 'built') && (
                        <button onClick={() => {
                          if (confirm(`Delete batch "${b.name}"?`)) deleteMut.mutate(b.id);
                        }} style={{ ...bulkBtnStyle, color: '#991b1b', borderColor: '#fca5a5' }}>
                          Delete
                        </button>
                      )}
                    </div>
                    {b.submitted_confirmation && (
                      <div style={{ fontSize: 10, color: '#065f46', marginTop: 4 }}>
                        FCC #: {b.submitted_confirmation}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: C.textFaint }}>
                  No batches yet. Select BSL Challenge candidates and click "📤 New batch" to stage your first filing.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Settings view ────────────────────────────────────────────────────────

function SettingsView() {
  const qc = useQueryClient();
  const settings = useQuery<ProviderSettings>({
    queryKey: ['bead-pm', 'provider-settings'],
    queryFn: () => api.get('/bead-perf-measures/provider-settings'),
  });
  const [form, setForm] = useState<Partial<ProviderSettings>>({});
  const [saved, setSaved] = useState(false);
  const current = { ...(settings.data ?? {} as ProviderSettings), ...form };

  const saveMut = useMutation({
    mutationFn: (patch: Partial<ProviderSettings>) =>
      api.put('/bead-perf-measures/provider-settings', patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bead-pm', 'provider-settings'] });
      setSaved(true); setForm({});
      setTimeout(() => setSaved(false), 2500);
    },
  });

  if (settings.isLoading) return <div style={{ padding: 40 }}>Loading…</div>;

  const field = (k: keyof ProviderSettings, label: string, hint?: string, type: string = 'text') => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.textMuted,
                      textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
        {label}
      </label>
      {hint && <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 6, maxWidth: 600 }}>{hint}</div>}
      {k === 'default_technology' ? (
        <select value={String(current[k] ?? '')}
                onChange={e => setForm(f => ({ ...f, [k]: parseInt(e.target.value, 10) as any }))}
                style={inputStyle}>
          <option value="50">50 — Optical / Fiber to the Premises</option>
          <option value="70">70 — Unlicensed Terrestrial Fixed Wireless</option>
          <option value="71">71 — Licensed Terrestrial Fixed Wireless</option>
          <option value="72">72 — Licensed-by-Rule Fixed Wireless</option>
          <option value="40">40 — Coaxial Cable / HFC</option>
          <option value="10">10 — Copper Wire</option>
          <option value="60">60 — Geostationary Satellite</option>
          <option value="61">61 — Non-geostationary Satellite</option>
          <option value="0">0 — Other</option>
        </select>
      ) : (
        <input type={type} value={String(current[k] ?? '')}
               onChange={e => setForm(f => ({ ...f, [k]: e.target.value as any }))}
               style={inputStyle} />
      )}
    </div>
  );

  return (
    <div style={{ marginTop: 24, maxWidth: 760 }}>
      <Card padding={28}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6 }}>
          Provider identity
        </div>
        <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.6, marginBottom: 22 }}>
          These values populate every BDC filing CSV row and the header of every evidence packet PDF.
          The BDC Provider ID is what the FCC assigns when you register as a broadband filer — it's
          shown in the BDC portal under your filer profile (not the same as your FRN). Sample values
          ship as defaults; replace with the real ones before you upload anything to the FCC.
        </div>
        {field('bdc_provider_id',    'BDC Provider ID',
               'Integer FCC assigns each broadband filer. Required for every CSV row.')}
        {field('brand_name',         'Brand name (as on FCC Broadband Map)',
               'The trade name shown to consumers on broadbandmap.fcc.gov.')}
        {field('default_technology', 'Default technology code',
               'Used as the per-row default. Override per row from the bulk toolbar if you have a mix.')}
        {field('isp_legal_name',     'ISP legal name',
               'Goes into the packet PDF header and audit trail.')}
        {field('fcc_frn',            'FCC Filer ID (FRN)',
               '10-digit FRN from CORES. Separate from the BDC Provider ID.')}
        {field('contact_name',       'Filer contact name')}
        {field('contact_email',      'Filer contact email', undefined, 'email')}
        {field('contact_phone',      'Filer contact phone', '000-000-0000 format. Optional.', 'tel')}
        {field('filer_kind',         'Filer kind',
               'e.g. "Provider (facilities-based)" — appears on the packet PDF.')}

        <div style={{ marginTop: 22, display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={() => saveMut.mutate(form)}
                  disabled={Object.keys(form).length === 0 || saveMut.isPending}
                  style={{
                    padding: '10px 22px', fontSize: 13, fontWeight: 600,
                    background: Object.keys(form).length === 0 ? C.border : '#1e3a8a',
                    color: 'white', border: 0, borderRadius: 5,
                    cursor: Object.keys(form).length === 0 ? 'not-allowed' : 'pointer',
                  }}>
            {saveMut.isPending ? 'Saving…' : 'Save'}
          </button>
          {saved && <span style={{ fontSize: 12, color: '#065f46', fontWeight: 600 }}>✓ Saved</span>}
          {settings.data?.updated_at && (
            <span style={{ fontSize: 11, color: C.textFaint }}>
              Last saved: {new Date(settings.data.updated_at).toLocaleString()}
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', maxWidth: 460, padding: '8px 11px',
  fontSize: 13, color: C.text,
  border: `1px solid ${C.border}`, borderRadius: 4,
  background: 'white',
};

function defaultQuarter() {
  const d = new Date();
  return `${d.getFullYear()}Q${Math.floor(d.getMonth() / 3) + 1}`;
}
